import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Col, Form, InputGroup, Row, Spinner, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faXmark } from "@fortawesome/free-solid-svg-icons";
import { rockAdminApi } from "../services/rockAdminApi";
import PaginationBar from "../components/common/PaginationBar";
import ErrorAlert from "../components/common/ErrorAlert";
import AdminUserDetailModal from "../components/admin/AdminUserDetailModal";

const ITEMS_PER_PAGE = 15;

const AVATAR_SIZE = 36;

function UserAvatar({ src, name }) {
  const boxStyle = {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    flexShrink: 0
  };

  if (src) {
    return (
      <div style={boxStyle}>
        <img
          alt={name}
          src={src}
          style={{
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            minWidth: AVATAR_SIZE,
            minHeight: AVATAR_SIZE,
            objectFit: "cover",
            borderRadius: "50%",
            border: "1px solid var(--tblr-border-color)",
            display: "block"
          }}
        />
      </div>
    );
  }
  const initials = (name || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      style={{
        ...boxStyle,
        borderRadius: "50%",
        background: "var(--tblr-primary)",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        fontSize: 13
      }}
    >
      {initials}
    </div>
  );
}

function StatBadge({ count, label }) {
  return (
    <div className="text-center" style={{ minWidth: 48 }}>
      <div className="fw-semibold">{count ?? "—"}</div>
      <div className="text-muted" style={{ fontSize: "0.7rem" }}>{label}</div>
    </div>
  );
}

export default function AdminUsersPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const data = await rockAdminApi.getUsers({ page, per_page: ITEMS_PER_PAGE, search });
        if (!cancelled) {
          setItems(data?.items ?? []);
          setTotal(data?.objects?.total ?? null);
        }
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [page, search]);

  const totalPages = useMemo(() => {
    if (typeof total === "number" && ITEMS_PER_PAGE > 0) {
      return Math.ceil(total / ITEMS_PER_PAGE);
    }
    return null;
  }, [total]);

  const handleSearchInput = useCallback((e) => {
    const val = e.target.value;
    setInputValue(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(val.trim());
      setPage(1);
    }, 400);
  }, []);

  const clearSearch = useCallback(() => {
    setInputValue("");
    setSearch("");
    setPage(1);
  }, []);

  return (
    <div className="page">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="page-pretitle">Admin</div>
              <h2 className="page-title">Users</h2>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <ErrorAlert error={error} title="Failed to load users" />

          <Card>
            <Card.Header>
              <InputGroup style={{ maxWidth: 320 }}>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faMagnifyingGlass} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search name or email…"
                  value={inputValue}
                  onChange={handleSearchInput}
                />
                {inputValue && (
                  <Button variant="outline-secondary" onClick={clearSearch}>
                    <FontAwesomeIcon icon={faXmark} />
                  </Button>
                )}
              </InputGroup>
            </Card.Header>

            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="table-vcenter card-table mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 56 }}></th>
                      <th>Name / Email</th>
                      <th style={{ width: 100 }}>Provider</th>
                      <th style={{ width: 180 }}>Sync Stats</th>
                      <th style={{ width: 130 }}>Last Synced</th>
                      <th style={{ width: 80 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="text-center py-4">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Loading...
                        </td>
                      </tr>
                    ) : items.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          {search ? "No users match your search." : "No users found."}
                        </td>
                      </tr>
                    ) : (
                      items.map((item) => (
                        <tr
                          key={item.user_id}
                          style={{ cursor: "pointer" }}
                          onClick={() => setSelectedUser(item)}
                        >
                          <td>
                            <UserAvatar src={item.avatar} name={item.name} />
                          </td>
                          <td>
                            <div className="fw-semibold">{item.name || "—"}</div>
                            <div className="text-muted small">{item.email}</div>
                          </td>
                          <td>
                            {item.provider ? (
                              <Badge bg="secondary" style={{ fontSize: "0.7rem", textTransform: "capitalize" }}>
                                {item.provider}
                              </Badge>
                            ) : "—"}
                          </td>
                          <td>
                            <div className="d-flex gap-3">
                              <StatBadge count={item.total_collections} label="Collections" />
                              <StatBadge count={item.total_my_rocks} label="Rocks" />
                              <StatBadge count={item.total_recent_snaps} label="Snaps" />
                            </div>
                          </td>
                          <td className="text-muted small">
                            {item.synced_at
                              ? new Date(item.synced_at).toLocaleDateString()
                              : "—"}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => setSelectedUser(item)}
                            >
                              Detail
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>

            <Card.Footer>
              <Row className="align-items-center gy-2">
                <Col>
                  <PaginationBar
                    page={page}
                    totalPages={totalPages}
                    canNext={totalPages ? page < totalPages : items.length >= ITEMS_PER_PAGE}
                    onPageChange={setPage}
                  />
                </Col>
                <Col xs="auto">
                  <div className="text-muted small">
                    Page <b>{page}</b>
                    {typeof total === "number" && (
                      <> · Total <b>{total}</b></>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Footer>
          </Card>
        </div>
      </div>

      <AdminUserDetailModal
        show={!!selectedUser}
        onHide={() => setSelectedUser(null)}
        user={selectedUser}
      />
    </div>
  );
}
